import { Link, useParams } from "react-router";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useDashboards } from "@/hooks/api";
import { dashboardComponents } from "@/components/dashboards/registry";

export function DashboardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useDashboards();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-admin-text-secondary p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Laster…
      </div>
    );
  }

  if (isError) {
    return (
      <DetailFrame id={id}>
        <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-6 text-sm text-admin-danger-400">
          Kunne ikke laste dashboards: {error?.message ?? "ukjent feil"}
        </div>
      </DetailFrame>
    );
  }

  const meta = data?.find((d) => d.id === id);
  const Component = id ? dashboardComponents[id] : undefined;

  if (!meta) {
    return (
      <DetailFrame id={id}>
        <div className="rounded-md border border-admin-card-border bg-admin-card p-6 text-sm text-admin-text-subtle">
          Fant ingen dashboard med id{" "}
          <code className="font-mono text-admin-text-secondary">{id}</code>.{" "}
          <Link to="/dashboards" className="text-admin-primary hover:underline">
            Tilbake til oversikten
          </Link>
          .
        </div>
      </DetailFrame>
    );
  }

  return (
    <div className="space-y-6">
      <DetailHeader meta={meta} />

      {!Component && (
        <div className="rounded-md border border-admin-card-border bg-admin-card p-6 text-sm text-admin-text-subtle">
          Denne dashboarden er registrert i backend, men har ingen frontend-komponent
          ennå. Legg til en oppføring i{" "}
          <code className="font-mono text-admin-text-secondary">
            components/dashboards/registry.tsx
          </code>
          .
        </div>
      )}

      {Component && <Component />}
    </div>
  );
}

function DetailFrame({
  id,
  children,
}: {
  id: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <BackLink />
        <h1 className="text-xl font-semibold">{id ?? "Dashboard"}</h1>
      </div>
      {children}
    </div>
  );
}

function DetailHeader({
  meta,
}: {
  meta: { title: string; description: string };
}) {
  return (
    <div className="space-y-2">
      <BackLink />
      <div>
        <h1 className="text-xl font-semibold">{meta.title}</h1>
        {meta.description && (
          <p className="text-sm text-admin-text-secondary mt-1">{meta.description}</p>
        )}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/dashboards"
      className="inline-flex items-center gap-1 text-xs text-admin-text-subtle hover:text-admin-text-secondary"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      Tilbake til oversikt
    </Link>
  );
}
